import { useMemo, useEffect, useRef } from 'react';

import Asterisk from '@decisiv/iconix/lib/components/Asterisk';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Message from '@decisiv/ui-components/lib/components/Message';
import Grid from '@decisiv/ui-components/lib/components/Grid';
import { TextField, Select } from 'components/Form';
import { useFormContext } from 'react-hook-form';

import { getCountries } from 'country-state-picker';
import { getStatesByCountry } from 'utils/countryRegions';

import { Trans, t } from '@lingui/macro';

import { FormContainer } from './styles';

const CLEAR_STATE_SELECT = t`Clear State`;
const ALLOWED_COUNTRIES = ['US', 'CA', 'EC', 'MX', 'CL', 'JP'];
const COUNTRY_OPTIONS = getCountries()
  .filter(({ code }: { code: string }) =>
    ALLOWED_COUNTRIES.includes(code.toUpperCase()),
  )
  .map(({ code, name }: { code: string; name: string }) => ({
    id: code.toUpperCase(),
    label: name,
    value: code.toUpperCase(),
  }));

export default function DepotForm() {
  const { watch, setValue } = useFormContext();
  const countryValue = watch('country');
  const previousCountryRef = useRef(countryValue);

  useEffect(() => {
    if (
      previousCountryRef.current &&
      previousCountryRef.current !== countryValue
    ) {
      setValue('state', '', { shouldValidate: true });
    }
    previousCountryRef.current = countryValue;
  }, [countryValue, setValue]);

  const stateOptions = useMemo(
    () => (countryValue ? getStatesByCountry(countryValue) : []),
    [countryValue],
  );

  return (
    <FormContainer>
      <Grid.Row>
        <Grid.Column>
          <Flex justifyContent="flex-end">
            <Message color="information" icon={Asterisk}>
              <Trans>Required Fields</Trans>
            </Message>
          </Flex>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <TextField
          label={t`ID`}
          name="id"
          helpMessage={t`Max 10 characters (alphanumeric only, no special characters)`}
          required
          disabled
        />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Name`} name="name" required />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Email`} name="email" required />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Address`} name="address" />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0}>
          <TextField label={t`City`} name="city" required />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0}>
          <Select
            clearButtonLabel={CLEAR_STATE_SELECT}
            label={t`State`}
            name="state"
            options={stateOptions}
            defaultValue=""
            required
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0}>
          <TextField label={t`Postal Code`} name="postalCode" />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0}>
          <Select
            label={t`Country`}
            name="country"
            options={COUNTRY_OPTIONS}
            defaultValue=""
            required
          />
        </Grid.Column>
      </Grid.Row>
    </FormContainer>
  );
}
